import {
  NOYAU_TEXTS_QUERY_KEY,
  NOYAU_TEXTS_USAGE_QUERY_KEY,
  NoyauTextsEditor,
} from '@/components/admin/reglages/noyau-texts-editor';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { fetchNoyauTexts, fetchNoyauTextsUsage, noyauTextsApi } from '@/lib/api';
import { NOYAU_TEXTS_ID } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * Réglages > Noyaux (noyau.md §6.3, refonte modulaire §11.6, issue #148): the
 * SIXTH tab of Réglages, and the editor of « Les textes des noyaux ».
 *
 * One versioned object, a singleton (N8): no list to open it from, so the tab
 * IS its editor. It reads the one detail; a 404 is the state without any
 * version (§6.4) and opens the empty editor, whose first save writes the
 * entity and its v1. The texts are provided by a specialist and typed here;
 * the repository writes none of them.
 *
 * ## Two reads, and the editor waits for both
 *
 * Beside the detail, the tab reads who plays which pointer of the texts
 * (`GET /api/noyau-texts/{id}/usage`, §6.5), which the save box and « Passer
 * en production » draw - before the v1 too, where the detail is a 404. The
 * editor mounts once both have landed, the régime of the persona editor and its
 * scale: a box drawn on a read still in flight would say « utilisé nulle part »
 * for as long as the read took. Either failing is the same one line.
 *
 * ## « Voir » travels in the URL
 *
 * `?version=<id>` opens a version read-only, the régime of the four other
 * editors and for their reason: a version somebody is reading is what gets
 * pasted into a thread. An unknown parameter degrades to « no version ».
 *
 * ## The editor's key is the entity's existence plus a counter
 *
 * The régime of `criteres.$criterionId.tsx`, and for its reason: the editor
 * seeds its draft from the detail ONCE, so a reload of a stale draft has to be
 * a REMOUNT, and a key carrying the draft's stamp would remount under the
 * caret at every autosave. The existence half is the singleton's own: the
 * first save writes the detail into the cache, and the empty editor gives way
 * to the entity's - the remount the criterion gets from its navigation.
 */

type NoyauxSearch = { version?: string };

function readSearch(search: Record<string, unknown>): NoyauxSearch {
  const raw = search.version;
  return typeof raw === 'string' && raw !== '' ? { version: raw } : {};
}

export const Route = createFileRoute('/admin/reglages/noyaux')({
  validateSearch: readSearch,
  component: ReglagesNoyauxRoute,
});

function ReglagesNoyauxRoute() {
  const { version } = Route.useSearch();
  const navigate = useNavigate();
  return (
    <ReglagesNoyauxPage
      version={version ?? null}
      onView={(versionId) =>
        void navigate({ to: '/admin/reglages/noyaux', search: { version: versionId } })
      }
      onCloseView={() => void navigate({ to: '/admin/reglages/noyaux', search: {} })}
    />
  );
}

export interface ReglagesNoyauxPageProps {
  /** The version « Voir » opened, from `?version=`, or null. */
  readonly version: string | null;
  readonly onView: (versionId: string) => void;
  readonly onCloseView: () => void;
}

/**
 * EXPORTED for `tests/reglages-noyaux.test.tsx`: `vitest.config.ts` leaves
 * `src/routes/**` out of the coverage REPORT, not out of the suite, and what
 * this page decides - the 404 read as « aucune version », the key of the
 * editor, the read-only view - is invisible from the components below it.
 */
export function ReglagesNoyauxPage({ version, onView, onCloseView }: ReglagesNoyauxPageProps) {
  const { email } = useAdminSession();
  /** Bumped by « Recharger le brouillon du serveur », and by nothing else. */
  const [reload, setReload] = useState(0);

  const texts = useQuery({
    queryKey: [NOYAU_TEXTS_QUERY_KEY, email, NOYAU_TEXTS_ID],
    queryFn: fetchNoyauTexts,
    retry: false,
  });

  /*
    Who reads which pointer of the texts (§6.5), read BESIDE the detail and
    waited for like it: the two boxes draw it, the box of the first save
    included - where the detail is a 404 and has nothing to say.
  */
  const usage = useQuery({
    queryKey: [NOYAU_TEXTS_USAGE_QUERY_KEY, email],
    queryFn: fetchNoyauTextsUsage,
    retry: false,
  });

  const viewed = useQuery({
    queryKey: ['noyau-texts-version', email, version],
    queryFn: () => noyauTextsApi.readVersion(NOYAU_TEXTS_ID, version ?? ''),
    // Nothing to read before the v1: no version id can be real yet.
    enabled: version !== null && texts.data !== undefined && texts.data !== null,
    retry: false,
  });

  const rejected = useRejectOn401(texts.error ?? usage.error);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="font-bold text-3xl text-bleu-nuit">Noyaux</h2>
        <p className="max-w-3xl text-gray-700 text-sm">
          Les dix-huit textes que montre le bloc <strong>Noyau</strong> : douze versions de deux
          textes, une par noyau, et six portraits. <strong>Un seul objet versionné</strong>, « Les
          textes des noyaux », sur la même couche que les expériences, les personas et les
          protocoles.
        </p>
      </header>

      {(texts.isPending || usage.isPending) && !texts.isError && !usage.isError ? (
        <div className="flex justify-center py-12">
          <PmsSpinner label="Chargement des textes des noyaux" />
        </div>
      ) : null}

      {(texts.isError || usage.isError) && !rejected ? (
        <p role="alert" className="text-rouge-deep text-sm">
          Les textes des noyaux n’ont pas pu être lus. Rechargez l’écran.
        </p>
      ) : null}

      {texts.data === undefined || usage.data === undefined ? null : (
        <NoyauTextsEditor
          // Existence and a counter, never the draft's stamp - see the head
          // of this file.
          key={`${texts.data === null ? 'vide' : 'textes'}-${reload}`}
          detail={texts.data}
          usage={usage.data}
          viewing={viewed.data ?? null}
          onView={(summary) => onView(summary.id)}
          onCloseView={onCloseView}
          onReload={() => {
            // The bump waits for the answer: a remount before it lands would
            // re-seed the editor from the stale draft it is escaping.
            void texts.refetch().then(() => setReload((was) => was + 1));
          }}
        />
      )}
    </div>
  );
}
